import { restoreErrorMessage } from "@/api/archived";
import {
  useCompleteConnectorOAuth,
  useConnectors,
  useDeleteConnector,
  useDisconnectConnector,
} from "@/api/connectors";
import { isApiError } from "@/api/utils";
import { ConnectorDialog } from "@/components/AdminPanel/Organization/components/connectors/ConnectorDialog";
import {
  chipButtonClassName,
  chipDangerButtonClassName,
  EmptySearch,
  EmptyState,
  LcBadge,
  NESTED_DIALOG_Z,
  PaneBody,
  paneFieldInputClassName,
  PaneHead,
  PaneSection,
  Row,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  SettingsInitialNavigation,
  useSettingsInitialNavigation,
} from "@/context/SettingsInitialNavigationContext";
import { cn } from "@/utils";
import analytics from "@/utils/analytics";
import { parseConnectorSlugFromPath } from "@/utils/settingsNavigation";
import { Connector } from "@cohesive/api-client";
import { IntegrationIcon, Spinner } from "@cohesive/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { ConnectorDetail } from "./ConnectorDetail";
import {
  CONNECTOR_OAUTH_CHANNEL,
  ConnectorOAuthCompletedMessage,
  RUN_CONNECT_MUTATION_KEY,
  RunConnectVariables,
  useRunConnect,
} from "./useRunConnect";

const ERROR_REASONS: Record<string, string> = {
  invalid_grant: "The sign-in was revoked or expired",
  expired: "The sign-in expired",
  unauthorized: "The provider rejected the credential",
  insufficient_scope: "The sign-in is missing a permission it needs",
  invalid_client: "The provider no longer recognizes this app",
  account_revoked: "The connected account was removed",
};

/**
 * The code and state the OAuth callback hands back in the landing link's fragment; `popup` when the
 * callback lost its popup's opener and landed inside the popup.
 */
function parseOAuthCallback(
  hash: string | undefined,
): { code: string; state: string; popup: boolean } | null {
  const params = new URLSearchParams(hash?.slice(1));
  const code = params.get("code");
  const state = params.get("state");
  if (!code || !state) return null;
  return { code, state, popup: params.get("popup") === "1" };
}

/** A `none` connector without inputs needs no connection: every user reaches it as-is. */
function needsConnection(connector: Connector): boolean {
  return connector.auth_method !== "none" || connector.inputs_schema.length > 0;
}

/** Whether a connect would be accepted: the API refuses a disabled or deprecated connector. */
function canConnect(connector: Connector): boolean {
  return connector.status === "enabled" && needsConnection(connector);
}

/** OAuth and Pipedream connectors without inputs have nothing to ask before the provider's window. */
function connectsInOneClick(connector: Connector): boolean {
  return (
    (connector.auth_method === "oauth" ||
      connector.auth_method === "pipedream") &&
    connector.inputs_schema.length === 0
  );
}

/** The row's second line: the user's connection, or what the connector is. */
export function describeConnector(connector: Connector): string {
  const connection = connector.connection;
  if (!connection) {
    if (!needsConnection(connector)) return "Ready to use · no sign-in needed";
    return connector.description ?? "";
  }
  if (connection.status === "error") {
    const reason =
      ERROR_REASONS[connection.last_error_code ?? ""] ??
      "The provider rejected this connection";
    return `${reason} — reconnect to keep using it`;
  }
  const account = connection.account_email
    ? ` as ${connection.account_email}`
    : "";
  return `Connected${account} · ${formatDistanceToNow(
    new Date(connection.created_at),
    { addSuffix: true },
  )}`;
}

/** Connects from a click and reports how it went. */
function useConnect() {
  const { run, isPending, connector: pending } = useRunConnect();
  const start = async (
    connector: Connector,
    inputs: Record<string, string> = {},
    fields: Record<string, string> = {},
  ) => {
    const name = connector.display_name;
    try {
      const outcome = await run({ connector, inputs, fields });
      if (outcome === "connected" || outcome === "connected-in-popup") {
        toast.success(`${name} connected.`);
      }
      return outcome;
    } catch (error) {
      const fallback = `Failed to connect ${name}. Please try again.`;
      toast.error(
        restoreErrorMessage(
          error,
          (!isApiError(error) && (error as Error).message) || fallback,
        ),
      );
    }
  };
  return { start, isPending, pending };
}

function ConnectDialog({
  connector,
  onClose,
}: {
  connector: Connector;
  onClose: () => void;
}) {
  const [inputs, setInputs] = useState<Record<string, string>>(
    connector.connection?.inputs ?? {},
  );
  const [fields, setFields] = useState<Record<string, string>>({});
  const { start, isPending } = useConnect();
  const fieldDefs = connector.fields ?? [];
  const name = connector.display_name;

  const complete =
    connector.inputs_schema.every(
      (input) => !input.required || inputs[input.name]?.trim(),
    ) && fieldDefs.every((field) => fields[field.name]?.trim());

  const submit = async () => {
    const trimmed = (values: Record<string, string>) =>
      Object.fromEntries(
        Object.entries(values)
          .map(([key, value]) => [key, value.trim()])
          .filter(([, value]) => value),
      );
    const outcome = await start(connector, trimmed(inputs), trimmed(fields));
    if (outcome === "connected" || outcome === "connected-in-popup") onClose();
  };

  const description = {
    oauth: `You'll sign in to ${name} in a new window.`,
    pipedream: `You'll connect your ${name} account through Pipedream.`,
    secret: `Your agents reach ${name} with this. It is stored encrypted and never shown again.`,
    none: `Your agents reach ${name} with these settings.`,
  }[connector.auth_method];

  // An authorization in flight finishes in another window: dismissing would only hide it.
  return (
    <Dialog open onOpenChange={() => !isPending && onClose()}>
      <DialogContent
        className={cn(
          "gap-6 rounded-xl p-6 sm:max-w-[32rem] sm:p-8",
          NESTED_DIALOG_Z,
        )}
        overlayClassName={NESTED_DIALOG_Z}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="inline-flex size-12 shrink-0 items-center justify-center rounded-md border border-n-10 bg-n-05"
          >
            <IntegrationIcon
              imageURL={connector.image_url}
              platform={connector.slug}
              name={name}
              className="size-6"
            />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <DialogTitle className="text-lg font-medium text-n-90">
              {connector.connection ? "Reconnect" : "Connect"} {name}
            </DialogTitle>
            {connector.description && (
              <p className="text-sm leading-relaxed text-n-60">
                {connector.description}
              </p>
            )}
          </div>
        </div>

        <form
          id="connector-connect-form"
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (complete && !isPending) submit();
          }}
        >
          <DialogDescription className="text-sm leading-relaxed text-n-70">
            {description}
          </DialogDescription>
          {connector.inputs_schema.map((input) => (
            <div key={input.name} className="flex flex-col gap-1.5">
              <Label htmlFor={`connector-input-${input.name}`}>
                {input.label}
              </Label>
              <Input
                id={`connector-input-${input.name}`}
                autoComplete="off"
                spellCheck={false}
                className={paneFieldInputClassName}
                value={inputs[input.name] ?? ""}
                onChange={(event) =>
                  setInputs({ ...inputs, [input.name]: event.target.value })
                }
              />
            </div>
          ))}
          {fieldDefs.map((field) => (
            <div key={field.name} className="flex flex-col gap-1.5">
              <Label htmlFor={`connector-field-${field.name}`}>
                {field.label}
              </Label>
              <Input
                id={`connector-field-${field.name}`}
                type="password"
                autoComplete="off"
                spellCheck={false}
                className={paneFieldInputClassName}
                value={fields[field.name] ?? ""}
                onChange={(event) =>
                  setFields({ ...fields, [field.name]: event.target.value })
                }
              />
            </div>
          ))}
        </form>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" disabled={isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="connector-connect-form"
            disabled={!complete || isPending}
            isLoading={isPending}
          >
            {connector.auth_method === "oauth" ||
            connector.auth_method === "pipedream"
              ? "Continue"
              : "Connect"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DisconnectDialog({
  connector,
  onClose,
}: {
  connector: Connector;
  onClose: () => void;
}) {
  const { mutateAsync: disconnect, isPending } = useDisconnectConnector();
  const name = connector.display_name;

  return (
    <Dialog open onOpenChange={() => !isPending && onClose()}>
      <DialogContent
        className={cn("sm:max-w-[425px]", NESTED_DIALOG_Z)}
        overlayClassName={NESTED_DIALOG_Z}
      >
        <DialogHeader>
          <DialogTitle>Disconnect {name}</DialogTitle>
          <DialogDescription>
            Your agents lose access to {name} until you connect it again.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" disabled={isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="bg-spectrum-red text-paper hover:bg-[color-mix(in_oklab,var(--spectrum-red)_88%,var(--ink))]"
            disabled={isPending}
            isLoading={isPending}
            onClick={async () => {
              try {
                await disconnect(connector);
                onClose();
                toast.success(`${name} disconnected.`);
              } catch {
                toast.error(`Failed to disconnect ${name}. Please try again.`);
              }
            }}
          >
            Disconnect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteConnectorDialog({
  connector,
  onClose,
}: {
  connector: Connector;
  onClose: () => void;
}) {
  const { mutateAsync: remove, isPending } = useDeleteConnector();
  const name = connector.display_name;

  return (
    <Dialog open onOpenChange={() => !isPending && onClose()}>
      <DialogContent
        className={cn("sm:max-w-[425px]", NESTED_DIALOG_Z)}
        overlayClassName={NESTED_DIALOG_Z}
      >
        <DialogHeader>
          <DialogTitle>Delete {name}</DialogTitle>
          <DialogDescription>
            This removes {name} and your connection to it, and can&apos;t be
            undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" disabled={isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="bg-spectrum-red text-paper hover:bg-[color-mix(in_oklab,var(--spectrum-red)_88%,var(--ink))]"
            disabled={isPending}
            isLoading={isPending}
            onClick={async () => {
              try {
                await remove(connector);
                onClose();
                toast.success(`${name} deleted.`);
              } catch {
                toast.error(`Failed to delete ${name}. Please try again.`);
              }
            }}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Connectors: React.FC = () => {
  const { data: connectors, isLoading, isError, isFetching } = useConnectors();
  const initialNavigation = useSettingsInitialNavigation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<Connector | null>(null);
  const [disconnecting, setDisconnecting] = useState<Connector | null>(null);
  // `null` is the create dialog.
  const [editing, setEditing] = useState<Connector | null>();
  const [deleting, setDeleting] = useState<Connector | null>(null);
  const [connectedInPopup, setConnectedInPopup] = useState<Connector | null>(
    null,
  );
  const {
    mutateAsync: completeOAuth,
    isPending: completing,
    variables: completion,
  } = useCompleteConnectorOAuth();
  const { start, pending: running } = useConnect();
  // A landing completion holds the list like a connect started here.
  const pending = running ?? (completing ? completion?.connector : undefined);
  const queryClient = useQueryClient();
  const landedInPopupRef = useRef(false);

  // A popup cut from its opener completes the connect after this window stopped waiting on it.
  useEffect(() => {
    const channel = new BroadcastChannel(CONNECTOR_OAUTH_CHANNEL);
    channel.onmessage = ({
      data,
    }: MessageEvent<ConnectorOAuthCompletedMessage>) => {
      if (data?.type !== "connector-oauth-completed") return;
      if (landedInPopupRef.current) return;
      // A connect still waiting on its popup reports the outcome itself.
      const waiting = queryClient.isMutating({
        mutationKey: RUN_CONNECT_MUTATION_KEY,
        predicate: (mutation) =>
          (mutation.state.variables as RunConnectVariables | undefined)
            ?.connector.id === data.connectorId,
      });
      if (waiting) return;
      setConnecting((open) => (open?.id === data.connectorId ? null : open));
      const connector = queryClient
        .getQueryData<Connector[]>(["connectors"])
        ?.find((c) => c.id === data.connectorId);
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
      if (connector) toast.success(`${connector.display_name} connected.`);
    };
    return () => channel.close();
  }, [queryClient]);

  const connect = (connector: Connector) => {
    if (connectsInOneClick(connector)) start(connector);
    else setConnecting(connector);
  };

  // A deep link acts once — the list refetches after a connect. It opens the connector's page. The
  // OAuth callback lands there with a code to complete; otherwise the link also opens the
  // connector's connect dialog unless it is already connected and healthy — a dialog even for a
  // one-click connector, since a popup needs a click. It waits out a refetch, as the list may be
  // restored from an old session. The shell clears the link on a tab switch.
  const handledNavigationRef = useRef<SettingsInitialNavigation>(undefined);
  useEffect(() => {
    const slug = parseConnectorSlugFromPath(initialNavigation.pathname);
    if (
      !slug ||
      !connectors ||
      isFetching ||
      handledNavigationRef.current === initialNavigation
    ) {
      return;
    }
    handledNavigationRef.current = initialNavigation;
    const connector = connectors.find((c) => c.slug === slug);
    const callback = parseOAuthCallback(initialNavigation.hash);
    if (!connector) {
      if (callback) toast.error("Failed to connect. Please try again.");
      return;
    }
    setSelectedId(connector.id);
    analytics.trackConnectorOpened({
      connectorId: connector.id,
      slug: connector.slug,
      source: "deep_link",
    });
    if (callback) {
      const name = connector.display_name;
      const { code, state, popup } = callback;
      landedInPopupRef.current = popup;
      completeOAuth({ connector, code, state }).then(
        () => {
          if (!popup) {
            toast.success(`${name} connected.`);
            return;
          }
          const channel = new BroadcastChannel(CONNECTOR_OAUTH_CHANNEL);
          channel.postMessage({
            type: "connector-oauth-completed",
            connectorId: connector.id,
          } satisfies ConnectorOAuthCompletedMessage);
          channel.close();
          window.close();
          // A browser may refuse once a COOP swap has cut the popup from its opener.
          if (!window.closed) setConnectedInPopup(connector);
        },
        (error) =>
          toast.error(
            restoreErrorMessage(
              error,
              `Failed to connect ${name}. Please try again.`,
            ),
          ),
      );
      return;
    }
    if (connector.connection?.status === "enabled") {
      toast.success(`${connector.display_name} is connected.`);
      return;
    }
    if (canConnect(connector)) setConnecting(connector);
  }, [initialNavigation, connectors, isFetching, completeOAuth]);

  const { matches, connected, available } = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const matches = (connectors ?? []).filter(
      (c) =>
        !term ||
        c.display_name.toLowerCase().includes(term) ||
        c.description?.toLowerCase().includes(term),
    );
    return {
      matches,
      connected: matches.filter((c) => c.connection),
      available: matches.filter((c) => !c.connection),
    };
  }, [connectors, searchTerm]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (connectedInPopup) {
    return (
      <EmptyState
        title={`${connectedInPopup.display_name} connected`}
        description="You can close this window."
      />
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-n-50">
        Could not load connectors. Refresh to try again.
      </div>
    );
  }

  // The verbs a connector's row and its page share.
  const connectorActions = (connector: Connector) => {
    // The user's own, which only they see and may edit.
    const own = connector.type === "organization-user";
    return (
      <>
        {connector.connection ? (
          <>
            {canConnect(connector) && (
              <Button
                variant="ghost"
                size="sm"
                className={chipButtonClassName}
                disabled={!!pending}
                isLoading={pending?.id === connector.id}
                onClick={() => connect(connector)}
              >
                Reconnect
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className={chipDangerButtonClassName}
              disabled={pending?.id === connector.id}
              onClick={() => setDisconnecting(connector)}
            >
              Disconnect
            </Button>
          </>
        ) : (
          canConnect(connector) && (
            <Button
              variant="secondary"
              size="sm"
              className={cn("shrink-0", smButtonClassName)}
              disabled={!!pending}
              isLoading={pending?.id === connector.id}
              onClick={() => connect(connector)}
            >
              Connect
            </Button>
          )
        )}
        {own && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Manage ${connector.display_name}`}
                className="shrink-0 text-n-50 hover:text-n-90"
                disabled={pending?.id === connector.id}
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {/* Deferred so the menu hands focus back before a dialog takes it. */}
              <DropdownMenuItem
                onSelect={() => window.setTimeout(() => setEditing(connector))}
              >
                <Pencil className="size-3.5 text-n-50" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-spectrum-red focus:text-spectrum-red data-[highlighted]:text-spectrum-red"
                onSelect={() => window.setTimeout(() => setDeleting(connector))}
              >
                <Trash2 className="size-3.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </>
    );
  };

  const connectorRow = (connector: Connector) => {
    const connection = connector.connection;
    // Listed only while the user is still connected; all that's left is to disconnect.
    const disabled = connector.status === "disabled";
    // The user's own, which only they see and may edit.
    const own = connector.type === "organization-user";
    return (
      <Row
        key={connector.id}
        tile={
          <IntegrationIcon
            imageURL={connector.image_url}
            platform={connector.slug}
            className={cn("size-5", disabled && "opacity-40 grayscale")}
          />
        }
        name={
          disabled ? (
            <span className="text-n-50">{connector.display_name}</span>
          ) : (
            connector.display_name
          )
        }
        onOpen={() => {
          setSelectedId(connector.id);
          analytics.trackConnectorOpened({
            connectorId: connector.id,
            slug: connector.slug,
            source: "list",
          });
        }}
        meta={
          disabled ? (
            "Disabled by admin"
          ) : (
            <>
              {/* A phone has no room for the label beside the verbs. */}
              {own && <span className="sm:hidden">Custom · </span>}
              {describeConnector(connector)}
            </>
          )
        }
      >
        {own && (
          <span className="hidden shrink-0 text-sm text-n-50 sm:inline">
            Custom
          </span>
        )}
        {connector.status === "deprecated" && (
          <LcBadge tone="warning">Deprecated</LcBadge>
        )}
        {!disabled && connection?.status === "error" && (
          <LcBadge tone="blocked">Error</LcBadge>
        )}
        {connectorActions(connector)}
      </Row>
    );
  };

  const selected = selectedId
    ? connectors?.find((c) => c.id === selectedId)
    : undefined;

  return (
    <PaneBody>
      {selected ? (
        <ConnectorDetail
          connector={selected}
          status={describeConnector(selected)}
          actions={connectorActions(selected)}
          reachable={
            selected.connection
              ? selected.connection.status === "enabled"
              : !needsConnection(selected)
          }
          onConnect={canConnect(selected) ? () => connect(selected) : undefined}
          pending={!!pending}
          connecting={pending?.id === selected.id}
          onBack={() => setSelectedId(null)}
        />
      ) : (
        <>
          <PaneHead
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search connectors"
          >
            <Button
              size="sm"
              variant="secondary"
              className={cn("shrink-0", smButtonClassName)}
              aria-label="Add custom connector"
              onClick={() => setEditing(null)}
            >
              <Plus />
              <span className="sm:hidden">Add</span>
              <span className="hidden sm:inline">Add custom connector</span>
            </Button>
          </PaneHead>

          {connectors?.length === 0 ? (
            <EmptyState
              title="No connectors yet"
              description="Connectors your organization adds show up here."
            />
          ) : matches.length === 0 ? (
            <EmptySearch
              query={searchTerm}
              noun="connectors"
              onClear={() => setSearchTerm("")}
            />
          ) : (
            <>
              {connected.length > 0 && (
                <PaneSection head="Connected">
                  {connected.map(connectorRow)}
                </PaneSection>
              )}
              {available.length > 0 && (
                <PaneSection head="Available">
                  {available.map(connectorRow)}
                </PaneSection>
              )}
            </>
          )}
        </>
      )}

      {connecting && (
        <ConnectDialog
          connector={connecting}
          onClose={() => setConnecting(null)}
        />
      )}

      {disconnecting && (
        <DisconnectDialog
          connector={disconnecting}
          onClose={() => setDisconnecting(null)}
        />
      )}

      {editing !== undefined && (
        <ConnectorDialog
          owner="user"
          connector={editing ?? undefined}
          onClose={() => setEditing(undefined)}
        />
      )}

      {deleting && (
        <DeleteConnectorDialog
          connector={deleting}
          onClose={() => setDeleting(null)}
        />
      )}
    </PaneBody>
  );
};

export default Connectors;
